import { useSearchParams } from "react-router-dom"
import {
  BusinessTypeEnum,
  SubscriptionBillingCycleEnum,
} from "~/__generated__/graphql"
import { Card, CardContent } from "~/shadcn/ui/card"
import { useDocumentTitle } from "~/common/useDocumentTitle"
import { DescriptiveLogo } from "../components/DescriptiveLogo"
import { SecondaryInfoSection } from "../components/SecondaryInfoSection"
import { CreateSelfServeUserForm } from "./components/CreateSelfServeUserForm"

// Maps the `type` query param handed off from the Duda marketing site to a
// business type. Explorer is sold to brands and marketing agencies only.
const BUSINESS_TYPE_BY_PARAM: Record<string, BusinessTypeEnum> = {
  brand: BusinessTypeEnum.Brand,
  marketing: BusinessTypeEnum.Marketing,
}

// Maps the `cycle` query param (the Annual/Monthly toggle on the Duda pricing
// page) to a billing cycle. Defaults to monthly when absent or unrecognized.
const BILLING_CYCLE_BY_PARAM: Record<string, SubscriptionBillingCycleEnum> = {
  monthly: SubscriptionBillingCycleEnum.Monthly,
  annual: SubscriptionBillingCycleEnum.Yearly,
  yearly: SubscriptionBillingCycleEnum.Yearly,
}

const param = (searchParams: URLSearchParams, key: string) =>
  (searchParams.get(key) ?? "").trim().toLowerCase()

export const SelfServeSignUpScreen = () => {
  useDocumentTitle("Create an Account")
  const [searchParams] = useSearchParams()
  const businessType = BUSINESS_TYPE_BY_PARAM[param(searchParams, "type")]
  const tierKey = param(searchParams, "tier") || null
  const billingCycle =
    BILLING_CYCLE_BY_PARAM[param(searchParams, "cycle")] ??
    SubscriptionBillingCycleEnum.Monthly

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="flex-1 flex bg-gray10 py-4 px-2">
        <div className="flex-1 mx-auto max-w-lg flex flex-col justify-center items-center">
          <DescriptiveLogo description="Create your account to get started on the influencer marketing platform built for brands and agencies." />

          <Card className="self-stretch">
            <CardContent className="p-8">
              <h1 className="font-header text-2xl text-center font-bold mb-8">
                Create an account
              </h1>
              <hr className="border-0 border-gray30 border-t-2 mb-6" />
              <div className="mx-8">
                {businessType ? (
                  <CreateSelfServeUserForm
                    businessType={businessType}
                    tierKey={tierKey}
                    billingCycle={billingCycle}
                  />
                ) : (
                  <p>
                    Sorry, this sign-up link is invalid. Please head back to the
                    Zano website and choose a plan to get started.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <SecondaryInfoSection />
    </div>
  )
}
